import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import { StepFullWidth } from 'components/Steps'
import CarouselControls from 'components/Carousel/CarouselControls'
import { Spacing20, Spacing40, Spacing50 } from 'styles/sharedStyle'
import { bp } from 'styles/breakpoints'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'

import {
  StepContainer,
  StepsContainer,
  StepSpacer,
  StyledSlider,
  Wrapper,
  CarouselControlsSpacer,
} from './VirtualServices.style'

import VirtualServicesTabs from './VirtualServicesTabs/VirtualServicesTabs'

const BREAKPOINT = 900

const VirtualServices = ({ services = [], villageSlug }) => {
  const slider = useRef(null)

  const [selectedServiceIndex, setSelectedServiceIndex] = useState(
    services.length > 0 ? 0 : -1
  )
  const [currentSlide, setCurrentSlide] = useState(0)

  const isBrowser = typeof window !== 'undefined'
  const carouselControlsVisible = useRef(
    isBrowser ? window.innerWidth >= BREAKPOINT : false
  )

  const rtl = useRef(false)

  const selectedService = useMemo(() => {
    if (selectedServiceIndex > -1 && selectedServiceIndex < services.length) {
      const service = services[selectedServiceIndex]

      service.stepsSorted = rtl ? [...service.steps].reverse() : service.steps

      return service
    }

    return null
  }, [services, selectedServiceIndex, rtl])

  const sliderRtlDetection = useCallback(() => {
    let intervalRef

    const rtlCheck = () => {
      const isRtl = document.querySelectorAll('html[dir="rtl"]').length > 0

      if (slider && isRtl) {
        rtl.current = isRtl
        setCurrentSlide(rtl ? selectedService.steps.length - 1 : 0)
        clearInterval(intervalRef)
      }
    }
    intervalRef = setInterval(rtlCheck, 1000)

    setTimeout(() => {
      clearInterval(intervalRef)
    }, 5000)
  }, [slider, selectedService, rtl])

  const onServiceTabClick = useCallback(
    (index) => {
      if (services[index]) {
        const setpsSorted = rtl
          ? [...services[index].steps].reverse()
          : services[index].steps

        trackGTM(
          'Virtual Shopping',
          'Virtual Shopping',
          `click`,
          setpsSorted[0].headline
        )

        GA4('virtual_shopping_steps_click', {
          headline: setpsSorted[0].headline,
          village_name: villageSlug,
        })

        setSelectedServiceIndex(index)
        setCurrentSlide(rtl.current ? services[index].steps.length - 1 : 0)
      }
    },
    [services]
  )

  const onCarouselControlClick = useCallback(
    (i, buttonPosition) => {
      setCurrentSlide(i)

      trackGTM(
        'Virtual Shopping',
        'Virtual Shopping',
        'carousel click',
        buttonPosition
      )

      if (slider.current) {
        slider.current.slickGoTo(i)
      }
    },
    [slider]
  )

  const onCarouselSwipeChange = useCallback(
    (i) => {
      GA4('virtual_shopping_steps_carousel_scroll', {
        village_name: villageSlug,
      })
      if (i !== currentSlide) {
        setCurrentSlide(i)
      }
    },
    [currentSlide]
  )

  const handleResize = () => {
    const shouldCarouselControlsBeVisible = window.innerWidth >= BREAKPOINT

    if (shouldCarouselControlsBeVisible !== carouselControlsVisible.current) {
      carouselControlsVisible.current = shouldCarouselControlsBeVisible
      setCurrentSlide(rtl.current ? selectedService.steps.length - 1 : 0)
    }
  }

  useEffect(() => {
    sliderRtlDetection()
  }, [slider])

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const settings = {
    arrows: false,
    draggable: true,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    focusOnSelect: true,
    initialSlide: rtl.current ? selectedService.steps.length - 1 : 0,
    rtl: false,
    centerMode: true,
    centerPadding: '0px',
    afterChange: onCarouselSwipeChange,
    responsive: [
      {
        breakpoint: parseInt(bp[1].replace('px', ''), 10),
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerPadding: '80px',
        },
      },
    ],
  }

  return (
    <Wrapper>
      <Spacing20 />
      <VirtualServicesTabs
        services={services}
        onServiceTabClick={onServiceTabClick}
      />
      <Spacing50 />
      <StepsContainer>
        {selectedService ? (
          <StyledSlider
            ref={(sliderRef) => {
              slider.current = sliderRef
            }}
            {...settings}
            key={`${selectedServiceIndex}${rtl.current}${
              carouselControlsVisible.current ? 'visible' : 'notvisible'
            }`}>
            {rtl.current && carouselControlsVisible.current && <StepSpacer />}
            {selectedService.stepsSorted.map((step, i) => (
              <StepContainer>
                <StepFullWidth
                  index={i + 1}
                  {...step}
                  description={{ description: step.description }}
                />
              </StepContainer>
            ))}
            {!rtl.current && carouselControlsVisible.current && <StepSpacer />}
          </StyledSlider>
        ) : null}
      </StepsContainer>
      <CarouselControlsSpacer />
      <CarouselControls
        onSlideChange={onCarouselControlClick}
        currentSlide={currentSlide}
        totalSlides={
          selectedService &&
          selectedService.steps &&
          selectedService.steps.length
        }
        hasMultipleItemsPerView={false}
        isBoxed={false}
        isSmall={false}
        villageName={villageSlug}
      />
      <Spacing40 />
    </Wrapper>
  )
}

VirtualServices.propTypes = {
  services: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      steps: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          headline: PropTypes.string,
          description: PropTypes.string,
        })
      ),
    })
  ),
  villageSlug: PropTypes.string,
}

VirtualServices.defaultProps = {
  services: [],
  villageSlug: '',
}

export default VirtualServices
